import { useEffect, useRef, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { useCartStore } from "@/stores/cart";
import { getVisitorId } from "@/lib/fingerprint";

/**
 * Tracks abandoned carts for CRM recovery campaigns.
 *
 * Works for both authenticated AND guest users:
 * - Authenticated: keyed by customer_id
 * - Guest: keyed by visitor_id (browser fingerprint)
 *
 * When a user has items in their cart and leaves the page
 * (tab hidden or page unload), a snapshot is upserted to `abandoned_carts`.
 *
 * When the user completes checkout, the abandoned cart is marked as recovered.
 */
export function useAbandonedCart() {
  const userIdRef = useRef<string | null>(null);
  const visitorIdRef = useRef<string | null>(null);
  const lastSnapshotRef = useRef<string>("");

  const snapshotAbandonedCart = useCallback(async () => {
    const userId = userIdRef.current;
    const visitorId = visitorIdRef.current;

    // Need at least one identifier
    if (!userId && !visitorId) return;

    const items = useCartStore.getState().items;
    if (items.length === 0) return;

    const totals = useCartStore.getState().getTotals();

    // Build a stable fingerprint to avoid duplicate writes
    const fingerprint = items
      .map((i) => `${i.product.id}:${i.quantity}`)
      .sort()
      .join(",");

    if (fingerprint === lastSnapshotRef.current) return;
    lastSnapshotRef.current = fingerprint;

    const cartSnapshot = items.map((item) => ({
      product_id: item.product.id,
      name: item.product.name,
      brand: item.product.brand,
      price_fcfa: item.product.price_fcfa,
      quantity: item.quantity,
      image_url: item.product.image_url,
    }));

    // Build the lookup filter — customer_id for auth users, visitor_id for guests
    const identityFilter = userId
      ? { column: "customer_id" as const, value: userId }
      : { column: "visitor_id" as const, value: visitorId! };

    const { error: selectError, data: existing } = await supabase
      .from("abandoned_carts")
      .select("id")
      .eq(identityFilter.column, identityFilter.value)
      .eq("recovered", false)
      .maybeSingle();

    if (selectError) return;

    const now = new Date().toISOString();

    if (existing) {
      await supabase
        .from("abandoned_carts")
        .update({
          cart_snapshot: cartSnapshot,
          total_fcfa: totals.total_ttc_fcfa,
          item_count: items.length,
          abandoned_at: now,
        })
        .eq("id", existing.id);
    } else {
      await supabase.from("abandoned_carts").insert({
        customer_id: userId ?? null,
        visitor_id: visitorId,
        cart_snapshot: cartSnapshot,
        total_fcfa: totals.total_ttc_fcfa,
        item_count: items.length,
        abandoned_at: now,
        recovered: false,
        recovery_status: "pending",
      });
    }
  }, []);

  // ── Track auth state + visitor ID ─────────────────────────────────────
  useEffect(() => {
    // Always resolve visitor ID (works for both guest and auth)
    getVisitorId().then((vid) => {
      visitorIdRef.current = vid;
    });

    supabase.auth.getUser().then(({ data: { user } }) => {
      userIdRef.current = user?.id ?? null;
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      userIdRef.current = session?.user?.id ?? null;
    });

    return () => subscription.unsubscribe();
  }, []);

  // ── Snapshot on page leave ──────────────────────────────────────────────
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        snapshotAbandonedCart();
      }
    };

    const handleBeforeUnload = () => {
      snapshotAbandonedCart();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [snapshotAbandonedCart]);
}

/**
 * Mark the current user's abandoned cart as recovered.
 * Call this after successful checkout. Works for auth and guest users.
 */
export async function markCartRecovered() {
  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (user) {
    await supabase
      .from("abandoned_carts")
      .update({ recovered: true, recovery_status: "recovered" })
      .eq("customer_id", user.id)
      .eq("recovered", false);
  } else {
    // Guest user — use visitor ID
    const visitorId = await getVisitorId();
    await supabase
      .from("abandoned_carts")
      .update({ recovered: true, recovery_status: "recovered" })
      .eq("visitor_id", visitorId)
      .eq("recovered", false);
  }
}
